import React, { useEffect, useRef } from 'react';
import { Button, Space, Tooltip } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';
import { escapePlainTextAsHtml, looksLikeHtml, sanitizeRichTextHtml } from '@/utils/richText';
import './FormattedTextArea.css';

export type FormattedTextAreaProps = {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  minHeight?: number;
  disabled?: boolean;
};

/**
 * Paste-with-format text field for document notes / T&C.
 * Stores sanitized HTML; plain legacy text still loads correctly.
 */
export const FormattedTextArea: React.FC<FormattedTextAreaProps> = ({
  value,
  onChange,
  placeholder,
  minHeight = 88,
  disabled,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef<string>('');

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const next = String(value ?? '');
    if (next === lastEmitted.current) return;
    const html = !next
      ? ''
      : looksLikeHtml(next)
        ? sanitizeRichTextHtml(next)
        : escapePlainTextAsHtml(next);
    if (el.innerHTML !== html) {
      el.innerHTML = html;
    }
    lastEmitted.current = next;
  }, [value]);

  const emit = () => {
    const el = editorRef.current;
    if (!el) return;
    const html = sanitizeRichTextHtml(el.innerHTML);
    const plain = (el.innerText || '').replace(/\u00a0/g, ' ').trim();
    const next = plain ? html : '';
    lastEmitted.current = next;
    onChange?.(next);
  };

  const run = (command: string) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false);
    emit();
  };

  const onPaste: React.ClipboardEventHandler<HTMLDivElement> = (e) => {
    if (disabled) return;
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    const insert = html
      ? sanitizeRichTextHtml(html, { dropEmptyBlocks: true })
      : escapePlainTextAsHtml(text);
    document.execCommand('insertHTML', false, insert || '');
    emit();
  };

  return (
    <div className={`formatted-text-area${disabled ? ' is-disabled' : ''}`}>
      <div className="formatted-text-area__toolbar">
        <Space size={4} wrap>
          <Tooltip title="Bold">
            <Button type="text" size="small" icon={<BoldOutlined />} disabled={disabled} onClick={() => run('bold')} />
          </Tooltip>
          <Tooltip title="Italic">
            <Button type="text" size="small" icon={<ItalicOutlined />} disabled={disabled} onClick={() => run('italic')} />
          </Tooltip>
          <Tooltip title="Underline">
            <Button type="text" size="small" icon={<UnderlineOutlined />} disabled={disabled} onClick={() => run('underline')} />
          </Tooltip>
          <Tooltip title="Bullet list">
            <Button
              type="text"
              size="small"
              icon={<UnorderedListOutlined />}
              disabled={disabled}
              onClick={() => run('insertUnorderedList')}
            />
          </Tooltip>
          <Tooltip title="Numbered list">
            <Button
              type="text"
              size="small"
              icon={<OrderedListOutlined />}
              disabled={disabled}
              onClick={() => run('insertOrderedList')}
            />
          </Tooltip>
        </Space>
        <span className="formatted-text-area__hint">Paste keeps formatting</span>
      </div>
      <div
        ref={editorRef}
        className="formatted-text-area__editor"
        contentEditable={!disabled}
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder || ''}
        style={{ minHeight }}
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        suppressContentEditableWarning
      />
    </div>
  );
};

export default FormattedTextArea;
